/**
 * Button Control Group Component
 *
 * A button styling control group with responsive support
 * for background color, text color, and padding.
 * Controls are organized into logical sub-groups:
 * - Background (Background Color, Gradient)
 * - Text (Typography)
 * - Spacing (Padding, Margin)
 * - Border (Border, Border Radius)
 * - Box Shadow
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import { PanelBody, ResetButton } from '@redfox-thank-you/components';
import { BaseControl, ColorPicker } from '@redfox-thank-you/controls';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { deviceTypeToBreakpoint, getResponsiveValues, updateResponsiveProperty } from '../common';
import {
	ButtonBorderControl,
	ButtonBoxShadowControl,
	ButtonGradientBackgroundControl,
	ButtonSpacingControl,
	ButtonTypographyControl,
} from './premium';
import type { ButtonControlProps, ButtonValues } from './types';

export const ButtonControl: React.FC< ButtonControlProps > = ( {
	attributes,
	setAttributes,
	title = __( 'Button Settings', 'redfox-thank-you' ),
	className = '',
	allowReset = true,
	initialOpen = false,
	enabledControls = {
		backgroundColor: true,
		textColor: true,
		gradientBackground: true,
		boxShadow: true,
		spacing: true,
		typography: true,
		border: true,
		responsive: true,
	},
	showIndividualResets = true,
	showGroupReset = true,
} ) => {
	// Get current device type for responsive controls
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	const activeBreakpoint = deviceTypeToBreakpoint( deviceType );

	/**
	 * Get current values for the active breakpoint
	 */
	const getCurrentValues = (): ButtonValues => {
		return getResponsiveValues< ButtonValues >( attributes, activeBreakpoint ) ?? {};
	};

	/**
	 * Get button values for current breakpoint
	 */
	const getButtonForBreakpoint = (
		buttonType: 'backgroundColor' | 'textColor',
	) => {
		const breakpointData = getResponsiveValues<ButtonValues>( attributes, activeBreakpoint );
		return breakpointData?.[ buttonType ];
	};

	/**
	 * Update button values for current breakpoint
	 */
	const updateButtonForBreakpoint = (
		buttonType: 'backgroundColor' | 'textColor',
		value: string | undefined,
	) => {
		const currentBreakpointData = attributes[ activeBreakpoint ] ?? {};

		const updatedBreakpointData = {
			...currentBreakpointData,
			[ buttonType ]: value,
		};

		setAttributes( {
			[ activeBreakpoint ]: updatedBreakpointData,
		} );
	};

	/**
	 * Generic attribute updater for premium controls
	 */
	const updateAttribute = ( key: string ) => (
		value: string | number | Record< string, unknown > | undefined,
	) => {
		const updateData = updateResponsiveProperty(
			attributes,
			key as keyof ButtonValues,
			value as ButtonValues[ keyof ButtonValues ],
			activeBreakpoint,
		);
		setAttributes( updateData );
	};

	/**
	 * Reset a property
	 */
	const resetProperty = ( property: string ) => {
		const updateData = updateResponsiveProperty(
			attributes,
			property as keyof ButtonValues,
			undefined,
			activeBreakpoint,
		);
		setAttributes( updateData );
	};

	/**
	 * Check if a property has a value
	 */
	const hasPropertyValue = ( property: string ): boolean => {
		const currentValues = getCurrentValues();
		return Boolean( currentValues[ property as keyof ButtonValues ] );
	};

	/**
	 * Reset all button values
	 */
	const resetAllButton = () => {
		const resetValues = {
			backgroundColor: undefined,
			textColor: undefined,
			gradientBackground: undefined,
			boxShadow: undefined,
			padding: undefined,
			margin: undefined,
			typography: undefined,
			border: undefined,
		};

		setAttributes( {
			desktop: resetValues,
			tablet: resetValues,
			mobile: resetValues,
		} );
	};

	/**
	 * Reset individual button property
	 */
	const resetIndividualButton =
		( buttonType: 'backgroundColor' | 'textColor' ) => () => {
			updateButtonForBreakpoint( buttonType, undefined );
		};

	/**
	 * Check if any button values are set
	 */
	const hasButtonValues = (): boolean => {
		const checkValues = ( breakpointData: ButtonValues | undefined ) => {
			if ( ! breakpointData ) return false;
			const {
				backgroundColor,
				textColor,
				gradientBackground,
				boxShadow,
				padding,
				margin,
				typography,
				border,
			} = breakpointData;

			return Boolean(
				backgroundColor ||
				textColor ||
				gradientBackground ||
				boxShadow ||
				padding ||
				margin ||
				typography ||
				border,
			);
		};

		return Boolean(
			checkValues( attributes.desktop ) ||
				checkValues( attributes.tablet ) ||
				checkValues( attributes.mobile ),
		);
	};

	/**
	 * Check if individual button property has values
	 */
	const hasButtonProperty = (
		buttonType: 'backgroundColor' | 'textColor',
	): boolean => {
		const breakpointData = getResponsiveValues<ButtonValues>( attributes, activeBreakpoint );

		if ( ! breakpointData ) return false;

		return Boolean( breakpointData[ buttonType ] );
	};

	/**
	 * Check if background group has any values
	 */
	const hasBackgroundValues = (): boolean => {
		const currentValues = getCurrentValues();
		return Boolean( currentValues.backgroundColor || currentValues.gradientBackground );
	};

	/**
	 * Reset background group values
	 */
	const resetBackgroundGroup = () => {
		updateButtonForBreakpoint( 'backgroundColor', undefined );
		resetProperty( 'gradientBackground' );
	};

	// Props for premium controls
	const premiumControlProps = {
		attributes,
		setAttributes,
		activeBreakpoint,
		currentValues: getCurrentValues(),
		updateAttribute,
		resetProperty,
		hasPropertyValue,
		showIndividualResets,
	};

	/**
	 * Render header actions (reset button)
	 */
	const renderHeaderActions = () => {
		return (
			<ResetButton
				onReset={ resetAllButton }
				show={ allowReset && showGroupReset && hasButtonValues() }
				label={ __( 'Reset Button', 'redfox-thank-you' ) }
				className="redfox-thank-you-button-control__reset"
			/>
		);
	};

	/**
	 * Check if background controls are enabled
	 */
	const isBackgroundGroupEnabled =
		enabledControls?.backgroundColor !== false ||
		enabledControls?.gradientBackground !== false;

	/**
	 * Check if typography controls are enabled
	 */
	const isTypographyGroupEnabled =
		enabledControls?.textColor !== false ||
		enabledControls?.typography !== false;

	return (
		<PanelBody
			title={ title }
			initialOpen={ initialOpen }
			className={ `redfox-thank-you-button-control ${ className }` }
			showResponsive={ enabledControls?.responsive !== false }
			responsiveProps={ {
				showLabels: false,
			} }
			headerActions={ renderHeaderActions() }
			isParentGroup
		>
			{ /* Background Group */ }
			{ isBackgroundGroupEnabled && (
				<PanelBody
					title={ __( 'Background', 'redfox-thank-you' ) }
					initialOpen={ false }
					className="redfox-thank-you-button-control__group redfox-thank-you-button-control__group--background"
					headerActions={
						<ResetButton
							onReset={ resetBackgroundGroup }
							show={ showGroupReset && hasBackgroundValues() }
							label={ __( 'Reset Background', 'redfox-thank-you' ) }
						/>
					}
				>
					{ /* Background Color Control */ }
					{ enabledControls?.backgroundColor !== false && (
						<BaseControl
							label={ __( 'Background Color', 'redfox-thank-you' ) }
							className="redfox-thank-you-button-control__background-color"
							showResetButton={ showIndividualResets }
							onReset={ resetIndividualButton( 'backgroundColor' ) }
							hasValue={ hasButtonProperty( 'backgroundColor' ) }
							inline
						>
							<ColorPicker
								value={ getButtonForBreakpoint( 'backgroundColor' ) }
								onChange={ ( value ) =>
									updateButtonForBreakpoint(
										'backgroundColor',
										value,
									)
								}
							/>
						</BaseControl>
					) }

					{ /* Gradient Background Control (Pro) */ }
					{ enabledControls?.gradientBackground !== false && (
						<ButtonGradientBackgroundControl { ...premiumControlProps } />
					) }
				</PanelBody>
			) }

			{ /* Text (Typography) Group */ }
			{ isTypographyGroupEnabled && (
				<PanelBody
					title={ __( 'Text', 'redfox-thank-you' ) }
					initialOpen={ false }
					className="redfox-thank-you-button-control__group redfox-thank-you-button-control__group--text"
					headerActions={
						<ResetButton
							onReset={ () => {
								updateButtonForBreakpoint( 'textColor', undefined );
								resetProperty( 'typography' );
							} }
							show={
								showGroupReset &&
								( hasButtonProperty( 'textColor' ) || hasPropertyValue( 'typography' ) )
							}
							label={ __( 'Reset Text', 'redfox-thank-you' ) }
						/>
					}
				>
					{ /* Text Color Control */ }
					{ enabledControls?.textColor !== false && (
						<BaseControl
							label={ __( 'Text Color', 'redfox-thank-you' ) }
							className="redfox-thank-you-button-control__text-color"
							showResetButton={ showIndividualResets }
							onReset={ resetIndividualButton( 'textColor' ) }
							hasValue={ hasButtonProperty( 'textColor' ) }
							inline
						>
							<ColorPicker
								value={ getButtonForBreakpoint( 'textColor' ) }
								onChange={ ( value ) =>
									updateButtonForBreakpoint( 'textColor', value )
								}
							/>
						</BaseControl>
					) }

					{ /* Typography Control (Pro) */ }
					{ enabledControls?.typography !== false && (
						<ButtonTypographyControl { ...premiumControlProps } />
					) }
				</PanelBody>
			) }

			{ /* Spacing Group */ }
			{ enabledControls?.spacing !== false && (
				<PanelBody
					title={ __( 'Spacing', 'redfox-thank-you' ) }
					initialOpen={ false }
					className="redfox-thank-you-button-control__group redfox-thank-you-button-control__group--spacing"
					headerActions={
						<ResetButton
							onReset={ () => {
								resetProperty( 'padding' );
								resetProperty( 'margin' );
							} }
							show={
								showGroupReset &&
								( hasPropertyValue( 'padding' ) || hasPropertyValue( 'margin' ) )
							}
							label={ __( 'Reset Spacing', 'redfox-thank-you' ) }
						/>
					}
				>
					<ButtonSpacingControl { ...premiumControlProps } />
				</PanelBody>
			) }

			{ /* Border Group */ }
			{ enabledControls?.border !== false && (
				<PanelBody
					title={ __( 'Border', 'redfox-thank-you' ) }
					initialOpen={ false }
					className="redfox-thank-you-button-control__group redfox-thank-you-button-control__group--border"
					headerActions={
						<ResetButton
							onReset={ () => resetProperty( 'border' ) }
							show={ showGroupReset && hasPropertyValue( 'border' ) }
							label={ __( 'Reset Border', 'redfox-thank-you' ) }
						/>
					}
				>
					<ButtonBorderControl { ...premiumControlProps } />
				</PanelBody>
			) }

			{ /* Box Shadow Group */ }
			{ enabledControls?.boxShadow !== false && (
				<PanelBody
					title={ __( 'Box Shadow', 'redfox-thank-you' ) }
					initialOpen={ false }
					className="redfox-thank-you-button-control__group redfox-thank-you-button-control__group--box-shadow"
					headerActions={
						<ResetButton
							onReset={ () => resetProperty( 'boxShadow' ) }
							show={ showGroupReset && hasPropertyValue( 'boxShadow' ) }
							label={ __( 'Reset Box Shadow', 'redfox-thank-you' ) }
						/>
					}
				>
					<ButtonBoxShadowControl { ...premiumControlProps } />
				</PanelBody>
			) }
		</PanelBody>
	);
};
